// src/components/teams/TeamsManager.jsx — layout minimal clean (v2, click-to-transfer + delete confirm)
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  TextInput,
  Select,
  Badge,
  Spinner,
  Dropdown,
  ToggleSwitch,
  Checkbox,
} from "flowbite-react";
import {
  HiPlus,
  HiSearch,
  HiTrash,
  HiPencil,
  HiUsers,
  HiOutlineX,
  HiViewGrid,
  HiDotsVertical,
} from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "../config/axiosInstance";
import { TEAMS, USERS } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";

/* ===================== Utils & Helpers ===================== */
function toArray(maybe) {
  if (Array.isArray(maybe)) return maybe;
  if (maybe?.rows && Array.isArray(maybe.rows)) return maybe.rows;
  if (maybe?.items && Array.isArray(maybe.items)) return maybe.items;
  if (maybe?.data && Array.isArray(maybe.data)) return maybe.data;
  return [];
}

function initials(str = "") {
  const s = String(str).trim();
  if (!s) return "?";
  const parts = s.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Paleta de cores que combinam com #E35412 (laranjas/quentes)
const AVATAR_COLORS = [
  "bg-[#E35412]", // principal
  "bg-[#F97316]", // laranja vivo
  "bg-[#FB923C]", // laranja mais claro
  "bg-[#EA580C]", // laranja queimado
  "bg-[#C2410C]", // mais escuro
];

const MEMBER_ROLE_OPTIONS = [
  { value: "member", label: "Membro" },
  { value: "admin", label: "Administrador do time" },
];

// Gera um índice pseudo-random estável a partir de uma string
function getAvatarColor(seed) {
  if (!seed) return AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0; // converte pra 32-bit
  }

  const idx = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

/* ===================== Component ===================== */
export default function TeamManagerPage() {
  const { user } = useApplication();
  const canEdit = user?.permissionId !== "USER";

  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);

  // Data
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  // cache leve de membros por time (count + preview)
  const [membersCache, setMembersCache] = useState({}); // { [teamId]: { loading: boolean, count: number|null, preview: User[] } }

  // UI state
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [density, setDensity] = useState("cozy"); // cozy | compact

  // Modals
  const [showEdit, setShowEdit] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuTeam, setMobileMenuTeam] = useState(null);
  const [teamMenuSlideIn, setTeamMenuSlideIn] = useState(false);

  // Modal de confirmação de exclusão
  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState(null);
  const [teamBoardCounts, setTeamBoardCounts] = useState({});

  // Form (Create/Edit Team)
  const [editingTeam, setEditingTeam] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formStatus, setFormStatus] = useState("1"); // 1 ativo, 0 inativo

  // Members mgmt
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]); // array de { userId, role }

  const updateMemberRole = (userId, role) => {
    setSelectedMembers((prev) =>
      prev.map((member) =>
        Number(member.userId) === Number(userId)
          ? { ...member, role: role || "member" }
          : member
      )
    );
  };

  /* ===================== Derived ===================== */
  const normalizedTeams = useMemo(() => toArray(teams), [teams]);

  const filteredTeams = useMemo(() => {
    let list = normalizedTeams.slice();
    if (sortBy === "name_asc")
      list = list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    if (sortBy === "name_desc")
      list = list
        .slice()
        .sort((a, b) => String(b.name).localeCompare(String(a.name)));
    if (sortBy === "status")
      list = list.slice().sort((a, b) => Number(b.status) - Number(a.status));

    return list;
  }, [normalizedTeams, search, sortBy]);

  const allUsers = useMemo(() => toArray(users), [users]);
  const allUsersMap = useMemo(() => {
    const map = new Map();
    allUsers.forEach((u) => {
      if (u?.id) map.set(u.id, u);
    });
    return map;
  }, [allUsers]);
  const selectedMemberIds = useMemo(
    () => new Set(selectedMembers.map((m) => Number(m.userId))),
    [selectedMembers]
  );
  const membersOfEditingTeam = useMemo(
    () =>
      selectedMembers.map((member) => ({
        ...member,
        user: allUsersMap.get(Number(member.userId)) || {
          id: member.userId,
        },
      })),
    [selectedMembers, allUsersMap]
  );
  const availableUsersFiltered = useMemo(() => {
    const q = memberSearch.toLowerCase().trim();
    return allUsers
      .filter(
        (u) =>
          !selectedMemberIds.has(u.id) &&
          (!q ||
            String(u.name || "")
              .toLowerCase()
              .includes(q) ||
            String(u.email || "")
              .toLowerCase()
              .includes(q))
      )
      .sort((a, b) =>
        String(a.name || a.email).localeCompare(String(b.name || b.email))
      )
      .slice(0, 100);
  }, [allUsers, memberSearch, selectedMemberIds]);

  /* ===================== API ===================== */
  const loadTeams = async (q = "") => {
    try {
      setLoading(true);
      const res = await axios.get(TEAMS.LIST, {
        params: {
          q,
        },
      });
      const payload = res?.data?.data;
      const list =
        toArray(payload?.items) ||
        toArray(payload?.data) ||
        toArray(res?.data) ||
        [];
      setTeams(list);
      list.forEach((team) => {
        fetchTeamBoardCount(team.id);
      });
    } catch (e) {
      console.error("Erro ao listar times:", e);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamsFromBackend = async () => {
    await loadTeams();
  };

  const handleApplyFilters = () => {
    loadTeams(search.trim());
  };

  const handleResetFilters = () => {
    setSearch("");
    setSortBy("name_asc");
    loadTeams("");
  };

  const loadUsers = async () => {
    try {
      setFetchingUsers(true);
      const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      const list =
        toArray(res?.data?.data?.data) ||
        toArray(res?.data?.data) ||
        toArray(res?.data) ||
        [];
      setUsers(list);
    } catch (e) {
      console.error("Erro ao buscar usuários:", e);
      setUsers([]);
    } finally {
      setFetchingUsers(false);
    }
  };

  const openCreate = () => {
    setEditingTeam(null);
    setFormName("");
    setFormDesc("");
    setFormStatus("1");
    setShowEdit(true);
  };

  const openEdit = (team) => {
    setEditingTeam(team);
    setFormName(team?.name || "");
    setFormDesc(team?.description || "");
    setFormStatus(String(team?.status ?? 1));
    setShowEdit(true);
  };

  const saveTeam = async () => {
    if (!formName.trim()) return;
    try {
      setSaving(true);
      const payload = {
        name: formName.trim(),
        description: formDesc || "",
        status: Number(formStatus),
      };
      let createdTeam = null;
      const isEditing = !!editingTeam?.id;
      if (isEditing) {
        await axios.put(TEAMS.UPDATE(editingTeam.id), payload);
      } else {
        const res = await axios.post(TEAMS.CREATE, payload);
        const rawTeam =
          res?.data?.data || res?.data?.team || res?.data?.item || res?.data;
        const normalizedCreated = Array.isArray(rawTeam)
          ? rawTeam[0] ?? null
          : rawTeam;
        createdTeam = normalizedCreated;
      }
      setShowEdit(false);
      await loadTeams();
      if (!isEditing && createdTeam?.id) {
        await openMembers(createdTeam);
      }
    } catch (e) {
      console.error("Erro ao salvar time:", e);
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async (teamId) => {
    if (!teamId) return;
    try {
      setDeletingId(teamId);
      await axios.delete(TEAMS.DELETE(teamId));
      await loadTeams();
    } catch (e) {
      console.error("Erro ao remover time:", e);
    } finally {
      setDeletingId(null);
    }
  };

  const openMembers = async (team) => {
    setEditingTeam(team);
    if (toArray(users).length === 0) await loadUsers();
    try {
      setSaving(true);
      const res = await axios.get(TEAMS.LIST_USERS(team.id));
      const list = toArray(res?.data?.data) || toArray(res?.data) || [];
      setSelectedMembers(
        list.map((m) => ({
          userId: Number(m.id),
          role: String(m?.TeamUser?.role || "member"),
        }))
      );
      // atualiza cache (count + preview até 4)
      setMembersCache((prev) => ({
        ...prev,
        [team.id]: {
          loading: false,
          count: list.length,
          preview: list.slice(0, 4),
        },
      }));
      setShowMembers(true);
    } catch (e) {
      console.error("Erro ao carregar membros do time:", e);
      setSelectedMembers([]);
      setShowMembers(true);
    } finally {
      setSaving(false);
    }
  };

  const persistMembers = async () => {
    if (!editingTeam?.id) return;
    try {
      setSaving(true);
      await axios.put(TEAMS.SET_USERS(editingTeam.id), {
        members: selectedMembers.map((member) => ({
          userId: Number(member.userId),
          role: member.role || "member",
        })),
      });
      setShowMembers(false);
      // mantém cache coerente
      const res = await axios.get(TEAMS.LIST_USERS(editingTeam.id));
      const list = toArray(res?.data?.data) || toArray(res?.data) || [];
      setMembersCache((prev) => ({
        ...prev,
        [editingTeam.id]: {
          loading: false,
          count: list.length,
          preview: list.slice(0, 4),
        },
      }));
    } catch (e) {
      console.error("Erro ao salvar membros:", e);
    } finally {
      setSaving(false);
    }
  };

  /* ====== handlers de confirmação ====== */
  const fetchTeamBoardCount = async (teamId, options = {}) => {
    const { updateConfirm = false } = options;
    if (!updateConfirm && teamBoardCounts[teamId] != null) return;
    let count = teamBoardCounts[teamId] ?? null;
    try {
      const res = await axios.get(TEAMS.COUNT_FUNNELS(teamId));
      count =
        Number(res?.data?.data?.count ?? res?.data?.count ?? res?.data ?? 0) ||
        0;
      setTeamBoardCounts((prev) => ({ ...prev, [teamId]: count }));
    } catch (error) {
      console.error("Erro ao carregar quadros do time:", error);
    }
    if (updateConfirm) {
      setConfirmDeleteTeam((prev) =>
        prev?.id === teamId
          ? { ...prev, boardsCount: count ?? 0, loadingBoards: false }
          : prev
      );
    }
  };

  const requestDelete = (team) => {
    if (!team?.id) return;
    const cachedCount = teamBoardCounts[team.id];
    setConfirmDeleteTeam({
      id: team.id,
      name: team.name,
      boardsCount: cachedCount ?? null,
      loadingBoards: cachedCount == null,
    });
    if (cachedCount == null) {
      fetchTeamBoardCount(team.id, { updateConfirm: true });
    }
  };

  const openMobileMenu = (team) => {
    setMobileMenuTeam(team);
    setMobileMenuOpen(true);
    window.requestAnimationFrame(() => setTeamMenuSlideIn(true));
  };

  const closeMobileMenu = () => {
    setTeamMenuSlideIn(false);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setMobileMenuTeam(null);
    }, 220);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteTeam?.id) return;
    const id = confirmDeleteTeam.id;
    setConfirmDeleteTeam(null);
    await deleteTeam(id);
  };

  /* ===================== Effects ===================== */
  useEffect(() => {
    loadTeams();
  }, []);

  /* ===================== Render ===================== */
  return (
    <div className="space-y-4">
      {/* Toolbar */}

      <header className="flex flex-col gap-1">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Times
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Gerencie equipes e membros
            </h1>
          </div>

          {canEdit && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="hidden md:flex items-center gap-2"
                onClick={openCreate}
              >
                Novo time
              </Button>
            </div>
          )}
        </div>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 shadow-sm p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1 min-w-[240px]">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <TextInput
              className="pl-10"
              placeholder="Buscar times..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button size="sm" onClick={handleApplyFilters}>
              Filtrar
            </Button>
            <Button size="sm" color="light" onClick={handleResetFilters}>
              Limpar
            </Button>
          </div>
        </div>
      </div>

      {/* Lista em Grid */}
      <div className="min-h-[220px]">
        {loading ? (
          <div
            className={`grid ${
              density === "compact" ? "gap-3" : "gap-5"
            } grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse"
              >
                <div className="h-5 w-24 bg-gray-100 rounded" />
                <div className="mt-3 h-4 bg-gray-100 rounded" />
                <div className="mt-2 h-4 w-2/3 bg-gray-100 rounded" />
                <div className="mt-4 h-8 bg-gray-100 rounded-xl" />
              </div>
            ))}
          </div>
        ) : filteredTeams.length === 0 ? (
          <EmptyState onCreate={canEdit ? openCreate : undefined} />
        ) : (
          <div
            className={`grid ${
              density === "compact" ? "gap-3" : "gap-5"
            } grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`}
          >
            <AnimatePresence>
              {filteredTeams.map((t) => (
                <TeamCard
                  key={t.id}
                  team={t}
                  canEdit={canEdit}
                  deletingId={deletingId}
                  openMembers={openMembers}
                  openEdit={openEdit}
                  onAskDelete={requestDelete}
                  membersCache={membersCache}
                  setMembersCache={setMembersCache}
                  density={density}
                  teamBoardCounts={teamBoardCounts}
                  onMobileMenuOpen={openMobileMenu}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {mobileMenuOpen && mobileMenuTeam && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={closeMobileMenu}
          />
          <div
            className={`absolute inset-x-0 bottom-0 rounded-t-3xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              teamMenuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                  Time
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {mobileMenuTeam.name}
                </p>
                <p className="text-sm text-slate-500">
                  {mobileMenuTeam.description || "Sem descrição disponível."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="text-gray-500"
                aria-label="Fechar menu"
              >
                <HiOutlineX className="text-2xl" />
              </button>
            </div>
            <div className="mt-6 space-y-3">
              {canEdit && (
                <>
                  <Button
                    className="w-full flex items-center justify-center gap-2"
                    onClick={() => {
                      closeMobileMenu();
                      openMembers(mobileMenuTeam);
                    }}
                  >
                    Gerenciar membros
                  </Button>
                  <Button
                    className="w-full flex items-center justify-center gap-2"
                    color="light"
                    onClick={() => {
                      closeMobileMenu();
                      openEdit(mobileMenuTeam);
                    }}
                  >
                    Editar time
                  </Button>
                  <Button
                    className="w-full flex items-center justify-center gap-2"
                    color="failure"
                    onClick={() => {
                      closeMobileMenu();
                      requestDelete(mobileMenuTeam);
                    }}
                  >
                    Remover time
                  </Button>
                </>
              )}
              {!canEdit && (
                <Button
                  className="w-full flex items-center justify-center gap-2"
                  color="light"
                  onClick={closeMobileMenu}
                >
                  Fechar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar/Editar */}
      <Modal show={showEdit} onClose={() => setShowEdit(false)}>
        <Modal.Header>{editingTeam ? "Editar time" : "Novo time"}</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Nome *
              </label>
              <TextInput
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex.: Comercial SP"
                maxLength={60}
              />
              <div className="flex justify-between">
                <p className="text-xs text-gray-500 mt-1">
                  Um nome curto e identificável para este time.
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {formName.length}/60
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Descrição
              </label>
              <TextInput
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Opcional"
                maxLength={120}
              />
              <div className="text-[10px] text-gray-400 mt-1 text-right">
                {formDesc.length}/120
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formStatus === "1"}
                label={formStatus === "1" ? "Ativo" : "Inativo"}
                onChange={(v) => setFormStatus(v ? "1" : "0")}
              />
              <label className="text-sm font-normal text-gray-700">
                Status do time (Ativo/Inativo)
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setShowEdit(false)}>
            Cancelar
          </Button>
          <Button onClick={saveTeam} disabled={saving || !formName.trim()}>
            {saving ? <Spinner size="sm" className="mr-2" /> : null}Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Membros — Click-to-Transfer */}
      <Modal
        show={showMembers}
        onClose={() => setShowMembers(false)}
        size="6xl"
      >
        <Modal.Header>
          Membros do time{editingTeam ? `: ${editingTeam.name}` : ""}
        </Modal.Header>
        <Modal.Body>
          {canEdit ? (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Disponíveis */}
              <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-100">
                  <div className="font-medium text-gray-800 text-sm">
                    Usuários disponíveis
                  </div>
                  <div className="relative">
                    <HiSearch className="absolute left-2 top-2.5 text-gray-400" />
                    <TextInput
                      className="pl-8 h-9"
                      placeholder="Buscar por nome ou e-mail"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="max-h-[460px] overflow-y-auto divide-y">
                  {fetchingUsers ? (
                    <div className="p-4 text-gray-500 flex items-center">
                      <Spinner size="sm" className="mr-2" /> Carregando
                      usuários…
                    </div>
                  ) : availableUsersFiltered.length === 0 ? (
                    <div className="p-4 text-gray-500">
                      Nenhum usuário encontrado.
                    </div>
                  ) : (
                    availableUsersFiltered.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          const id = Number(u.id);
                          setSelectedMembers((prev) =>
                            prev.some((member) => Number(member.userId) === id)
                              ? prev
                              : [...prev, { userId: id, role: "member" }]
                          );
                        }}
                        className="w-full text-left flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        title="Clique para adicionar"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-xl bg-gray-800 text-white text-xs font-bold flex items-center justify-center">
                            {initials(u.name || u.email)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {u.name || u.email}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {u.email}
                            </div>
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-500">
                          adicionar
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Selecionados */}
              <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                  <div className="font-medium text-gray-800 text-sm flex items-center gap-2">
                    <span>Selecionados</span>
                    <Badge color="gray" className="w-fit">
                      {selectedMembers.length}
                    </Badge>
                  </div>
                  {selectedMembers.length > 0 && (
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => setSelectedMembers([])}
                      className="flex items-center bg-gray-500 text-white"
                    >
                      <div className="flex items-center">
                        <HiOutlineX className="mr-1" /> <span>Limpar tudo</span>
                      </div>
                    </Button>
                  )}
                </div>
                <div className="max-h-[460px] overflow-y-auto divide-y">
                  {membersOfEditingTeam.length === 0 ? (
                    <div className="p-4 text-gray-500">
                      Nenhum membro adicionado.
                    </div>
                  ) : (
                    membersOfEditingTeam.map((member) => {
                      const user = member.user || {};
                      const displayName =
                        user.name || user.email || `#${member.userId}`;
                      const displayEmail = user.email || "-";
                      return (
                        <div
                          key={member.userId}
                          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-8 w-8 rounded-xl bg-gray-900 text-white text-xs font-bold flex items-center justify-center">
                              {initials(displayName)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {displayName}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {displayEmail}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              size="xs"
                              className="text-xs w-36"
                              value={member.role || "member"}
                              onChange={(e) =>
                                updateMemberRole(member.userId, e.target.value)
                              }
                            >
                              {MEMBER_ROLE_OPTIONS.map((roleOption) => (
                                <option
                                  key={roleOption.value}
                                  value={roleOption.value}
                                >
                                  {roleOption.label}
                                </option>
                              ))}
                            </Select>
                            <button
                              type="button"
                              onClick={() => {
                                const id = Number(member.userId);
                                setSelectedMembers((prev) =>
                                  prev.filter((m) => Number(m.userId) !== id)
                                );
                              }}
                              className="text-[11px] text-gray-500 underline"
                            >
                              remover
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Somente visualização
            <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
              <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                <div className="font-medium text-gray-800 text-sm flex items-center gap-2">
                  <span>Membros do time</span>
                  <Badge color="gray" className="w-fit">
                    {selectedMembers.length}
                  </Badge>
                </div>
              </div>
              <div className="max-h-[460px] overflow-y-auto divide-y">
                {membersOfEditingTeam.length === 0 ? (
                  <div className="p-4 text-gray-500">
                    Nenhum membro adicionado.
                  </div>
                ) : (
                  membersOfEditingTeam.map((member) => {
                    const user = member.user || {};
                    const displayName =
                      user.name || user.email || `#${member.userId}`;
                    const displayEmail = user.email || "-";
                    const roleLabel =
                      MEMBER_ROLE_OPTIONS.find(
                        (opt) => opt.value === member.role
                      )?.label || "Membro";
                    return (
                      <div
                        key={member.userId}
                        className="flex items-center justify-between px-3 py-2"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-xl bg-gray-900 text-white text-xs font-bold flex items-center justify-center">
                            {initials(displayName)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {displayName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {displayEmail}
                            </div>
                          </div>
                        </div>
                        <Badge color="gray" className="text-[11px]">
                          {roleLabel}
                        </Badge>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        {!canEdit ? null : (
          <Modal.Footer className="w-full">
            <div className="flex justify-center gap-2 w-full">
              <Button
                className="px-4"
                color="gray"
                onClick={() => setShowMembers(false)}
              >
                Fechar
              </Button>
              <Button onClick={persistMembers} disabled={saving}>
                {saving ? <Spinner size="sm" className="mr-2" /> : null}
                Salvar alterações
              </Button>
            </div>
          </Modal.Footer>
        )}
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal
        show={!!confirmDeleteTeam}
        onClose={() => setConfirmDeleteTeam(null)}
      >
        <Modal.Header>Confirmar exclusão</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <p className="text-sm text-center text-gray-700">
              Tem certeza de que deseja apagar o time{" "}
              <span className="font-semibold">
                {confirmDeleteTeam?.name || `#${confirmDeleteTeam?.id}`}
              </span>
              ?
            </p>
            <p className="text-xs text-gray-500 text-center">
              Essa ação é permanente e não poderá ser desfeita.
            </p>
            {confirmDeleteTeam && (
              <p className="text-xs text-gray-500 text-center">
                {confirmDeleteTeam.loadingBoards
                  ? "Carregando quantos quadros estão vinculados…"
                  : `Esse time possui ${
                      confirmDeleteTeam.boardsCount ?? 0
                    } quadro${
                      confirmDeleteTeam.boardsCount === 1 ? "" : "s"
                    } vinculados.`}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-center w-full gap-2">
            <Button color="gray" onClick={() => setConfirmDeleteTeam(null)}>
              Cancelar
            </Button>
            <Button
              color="failure"
              onClick={confirmDelete}
              disabled={deletingId === confirmDeleteTeam?.id}
            >
              <div className="flex gap-2 items-center">
                {deletingId === confirmDeleteTeam?.id ? (
                  <>
                    <Spinner size="sm" className="mr-1" />{" "}
                    <span>Apagando…</span>
                  </>
                ) : (
                  <>
                    <HiTrash /> <span>Apagar</span>
                  </>
                )}
              </div>
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {canEdit && (
        <Button
          className="primary fixed md:hidden bottom-5 right-4 h-14 w-14 rounded-full shadow-lg flex items-center justify-center"
          size="sm"
          onClick={openCreate}
        >
          <HiPlus className="text-2xl" />
        </Button>
      )}
    </div>
  );
}

/* ===================== Sub-components ===================== */
function TeamCard({
  team,
  canEdit,
  deletingId,
  openMembers,
  openEdit,
  onAskDelete, // <-- novo: abre modal de confirmação
  membersCache,
  setMembersCache,
  density = "cozy",
  teamBoardCounts,
  onMobileMenuOpen,
}) {
  const [visible, setVisible] = useState(false);
  const boardCount = teamBoardCounts?.[team.id];

  // lazy fetch de membros quando o card entra em viewport
  useEffect(() => {
    const el = document.getElementById(`team-card-${team.id}`);
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            if (!membersCache[team.id]) {
              try {
                setMembersCache((prev) => ({
                  ...prev,
                  [team.id]: { loading: true, count: 0, preview: [] },
                }));
                const res = await axios.get(TEAMS.LIST_USERS(team.id));
                const list =
                  toArray(res?.data?.data) || toArray(res?.data) || [];
                setMembersCache((prev) => ({
                  ...prev,
                  [team.id]: {
                    loading: false,
                    count: list.length,
                    preview: list.slice(0, 4),
                  },
                }));
              } catch (e) {
                setMembersCache((prev) => ({
                  ...prev,
                  [team.id]: { loading: false, count: 0, preview: [] },
                }));
              }
            }
          }
        });
      },
      { rootMargin: "0px 0px -20% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [team?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cache = membersCache[team.id] || {
    loading: false,
    count: null,
    preview: [],
  };

  const pad = density === "compact" ? "p-3" : "p-5";
  const gapTop = density === "compact" ? "mt-2" : "mt-3";

  const handleCardClick = (event) => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) {
      event.stopPropagation();
      onMobileMenuOpen?.(team);
    }
  };

  return (
    <motion.div
      id={`team-card-${team.id}`}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`group relative rounded-2xl border border-gray-200 bg-white ${pad} shadow-sm hover:shadow-md hover:border-gray-300 transition-all`}
      onClick={handleCardClick}
    >
      {/* Top row */}
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-gray-900 text-white flex items-center justify-center text-sm font-bold">
          {initials(team.name || "Time")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {team.name}
            </div>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full border ${
                Number(team.status) === 1
                  ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                  : "border-gray-200 text-gray-600 bg-gray-50"
              }`}
            >
              {Number(team.status) === 1 ? "Ativo" : "Inativo"}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <HiViewGrid className="text-[13px]" />
            <span>
              {typeof boardCount === "number"
                ? `${boardCount} quadro${boardCount === 1 ? "" : "s"}`
                : "Consultando quadros…"}
            </span>
          </div>
        </div>
        {canEdit && (
          <div className="shrink-0">
            <Dropdown
              inline
              label={<HiDotsVertical className="text-gray-500 hidden md:block" />}
              arrowIcon={false}
            >
              <Dropdown.Item icon={HiUsers} onClick={() => openMembers(team)}>
                Gerenciar membros
              </Dropdown.Item>
              <Dropdown.Item icon={HiPencil} onClick={() => openEdit(team)}>
                Editar
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                icon={HiTrash}
                className="text-red-600"
                onClick={() => onAskDelete?.(team)} // abre confirmação
              >
                Remover
              </Dropdown.Item>
            </Dropdown>
          </div>
        )}
      </div>

      {/* Description */}
      <div
        className={`${gapTop} text-sm text-gray-700 line-clamp-2 min-h-[2.5rem]`}
      >
        {team.description || "Sem descrição."}
      </div>

      {/* Footer */}
      <div className={`${gapTop} flex items-center justify-between`}>
        <div className="flex -space-x-3">
          {cache.preview.slice(0, 4).map((u, index) => (
            <div
              key={u.id}
              className={`
          h-8 w-8 rounded-full 
          ${getAvatarColor(index)}
          text-white text-[10px] font-bold 
          flex items-center justify-center 
          ring-2 ring-white
        `}
            >
              {initials(u.name || u.email)}
            </div>
          ))}
          {cache.count > 4 && (
            <div className="h-8 w-8 rounded-full bg-gray-200 text-gray-800 text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">
              +{cache.count - 4}
            </div>
          )}
          {cache.loading && (
            <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse ring-2 ring-white" />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-4 shadow-sm">
        <HiUsers className="text-gray-500 text-xl" />
      </div>
      <div className="text-gray-900 font-semibold text-lg">
        Nenhum time encontrado
      </div>
      <div className="text-gray-500 text-sm mt-1">
        Crie seu primeiro time para organizar membros e funis.
      </div>
      {onCreate && (
        <div className="mt-5 flex justify-center items-center">
          <Button onClick={onCreate}>
            <div className="flex items-center">
              <HiPlus className="mr-1" />
              <span>Criar time</span>
            </div>
          </Button>
        </div>
      )}
    </div>
  );
}
